import { useEffect } from 'react';
import type { PrintReceiptRaster, ReceiptDto, StationTicket } from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { api } from '@/lib/api';
import { uploadRaster } from './rasterUpload';
import { receiptToRaster, stationTicketToRaster } from './receiptCanvas';

/**
 * The drawing service for print jobs no browser has drawn (owner: portal
 * orders "print the wrong receipt layout — it's still using the old version").
 *
 * A PORTAL order is placed by the customer's PC, which has no staff session
 * and cannot be trusted to read print payloads — so nobody drew its ticket,
 * and after the grace it printed the typed fallback. The staff app is a
 * browser that is open all day at the counter: it polls the undrawn queue,
 * paints each job with the SAME layout code the till uses for its own
 * receipts, and uploads the image. The server dispatches on upload, so the
 * ticket leaves the queue the moment any staff tab has drawn it.
 *
 * Everything effectful is injected so the loop is testable without a DOM.
 */

export interface UndrawnJob {
  id: string;
  kind: string;
  payload: unknown;
}

export interface DrawJobDeps {
  fetchUndrawn: () => Promise<UndrawnJob[]>;
  attachRaster: (jobId: string, raster: PrintReceiptRaster) => Promise<void>;
  toRaster: (job: UndrawnJob) => PrintReceiptRaster | null;
}

/**
 * One pass: draw and upload every job not yet attempted by THIS tab.
 *
 * `attempted` belongs to the caller and outlives the pass — a job that fails
 * to draw (payload from a future version, canvas refused) is remembered and
 * never retried here, because retrying would redraw the same wrong thing
 * forever. The server's typed fallback prints it instead. Two staff tabs
 * racing the same job is safe: the raster endpoint takes the first and
 * answers `print_job_already_sent` to the second, which lands in the same
 * silent-skip path.
 */
export async function drawUndrawnPrintJobs(
  deps: DrawJobDeps,
  attempted: Set<string>,
): Promise<number> {
  let drawn = 0;
  const jobs = await deps.fetchUndrawn();
  for (const job of jobs) {
    if (attempted.has(job.id)) continue;
    attempted.add(job.id);

    const raster = deps.toRaster(job);
    if (!raster) continue;

    try {
      await deps.attachRaster(job.id, raster);
      drawn += 1;
    } catch {
      // Lost the race to another tab, or the job dispatched meanwhile. The
      // paper is somebody else's now either way.
    }
  }
  return drawn;
}

/** The real wiring: the api client and the canvas painters. */
export function defaultDrawJobDeps(): DrawJobDeps {
  return {
    fetchUndrawn: () => api.get<UndrawnJob[]>('/print-jobs/undrawn'),
    // Deflated on the way out when the browser can — see `rasterUpload.ts`.
    attachRaster: (jobId, raster) => uploadRaster(jobId, raster),
    toRaster: (job) =>
      job.kind === 'RECEIPT'
        ? receiptToRaster(job.payload as ReceiptDto)
        : job.kind === 'STATION_TICKET'
          ? stationTicketToRaster(job.payload as StationTicket)
          : null,
  };
}

const POLL_MS = 3_000;

/**
 * Mounted once in the app shell. Quiet: a pass that finds nothing costs one
 * small GET, and the interval only runs while somebody is signed in — the
 * endpoint would 403 an anonymous tab anyway.
 */
export function useUndrawnTicketDrawer(): void {
  const { employee, can } = useAuth();
  const enabled = !!employee && can('sessions.manage');

  useEffect(() => {
    if (!enabled) return;
    const attempted = new Set<string>();
    const deps = defaultDrawJobDeps();
    let busy = false;

    const tick = () => {
      if (busy) return;
      busy = true;
      void drawUndrawnPrintJobs(deps, attempted)
        .catch(() => {
          // A failed poll (offline, server restarting) is the next poll's
          // problem; the typed fallback keeps the venue printing meanwhile.
        })
        .finally(() => {
          busy = false;
        });
    };

    tick();
    const timer = setInterval(tick, POLL_MS);
    return () => clearInterval(timer);
  }, [enabled]);
}
